import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureError } from './sentry';
import { log } from '@utils/logger';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error('Uncaught error in tree:', error, info.componentStack);
    captureError(error);
  }

  reset = (): void => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-gray-950 p-8 text-white">
        <h1 className="text-2xl font-semibold">出错了</h1>
        <p className="max-w-xl text-center text-sm text-white/60">{error.message}</p>
        <button
          type="button"
          onClick={this.reset}
          className="bg-brand hover:bg-brand-900 rounded-md px-4 py-2 text-sm font-medium"
        >
          重试
        </button>
      </div>
    );
  }
}
